import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import * as pdfJs from "pdfjs-dist";

pdfJs.GlobalWorkerOptions.workerSrc = "/pdfjs/pdf.worker.min.js";

const RENDER_SCALE = 2;
const MAX_RETRIES = 20;
const RETRY_DELAY_MS = 3000;
const MAX_ZOOM = 3;
const DEFAULT_ZOOM = 1;
const COMPACT_DEFAULT_ZOOM = 0.72;
const COMPACT_MIN_ZOOM = 0.6;

function getDistance(touches) {
  const dx = touches[0].clientX - touches[1].clientX;
  const dy = touches[0].clientY - touches[1].clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

function getMidpoint(touches) {
  return {
    x: (touches[0].clientX + touches[1].clientX) / 2,
    y: (touches[0].clientY + touches[1].clientY) / 2,
  };
}

export default function MobilePdfViewer({ url, compact }) {
  const wrapperRef = useRef(null);
  const spacerRef = useRef(null);
  const contentRef = useRef(null);
  const [status, setStatus] = useState("loading");
  const [pageCount, setPageCount] = useState(0);

  const baseSizeRef = useRef({ width: 0, height: 0 });
  const zoomRef = useRef(1);
  const startDistRef = useRef(0);
  const startZoomRef = useRef(1);
  const panRef = useRef(null);
  const minZoom = compact ? COMPACT_MIN_ZOOM : DEFAULT_ZOOM;
  const initialZoom = compact ? COMPACT_DEFAULT_ZOOM : DEFAULT_ZOOM;

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const applyScale = (scale) => {
      const spacer = spacerRef.current;
      const content = contentRef.current;
      const base = baseSizeRef.current;
      if (!spacer || !content || !base.width) return;

      content.style.transform = `scale(${scale})`;
      spacer.style.width = `${base.width * scale}px`;
      spacer.style.height = `${base.height * scale}px`;
    };

    const setZoom = (newScale, focalX, focalY) => {
      const clamped = Math.min(MAX_ZOOM, Math.max(minZoom, newScale));
      const oldScale = zoomRef.current;
      const spacer = spacerRef.current;
      if (clamped === oldScale || !spacer) return;

      const wrapperRect = el.getBoundingClientRect();
      const viewX = focalX - wrapperRect.left;
      const viewY = focalY - wrapperRect.top;
      const oldOffsetLeft = spacer.offsetLeft;
      const oldOffsetTop = spacer.offsetTop;

      const contentX = (el.scrollLeft + viewX - oldOffsetLeft) / oldScale;
      const contentY = (el.scrollTop + viewY - oldOffsetTop) / oldScale;

      zoomRef.current = clamped;
      applyScale(clamped);

      el.scrollLeft = Math.max(0, spacer.offsetLeft + contentX * clamped - viewX);
      el.scrollTop = Math.max(0, spacer.offsetTop + contentY * clamped - viewY);
    };

    const isZoomed = () => {
      return el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1;
    };

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        startDistRef.current = getDistance(e.touches);
        startZoomRef.current = zoomRef.current;
        panRef.current = null;
        return;
      }

      if (e.touches.length === 1) {
        panRef.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
          scrollLeft: el.scrollLeft,
          scrollTop: el.scrollTop,
        };
      }
    };

    const onTouchMove = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const dist = getDistance(e.touches);
        const mid = getMidpoint(e.touches);
        setZoom((dist / startDistRef.current) * startZoomRef.current, mid.x, mid.y);
        return;
      }

      if (e.touches.length === 1 && panRef.current && isZoomed()) {
        e.preventDefault();
        const dx = e.touches[0].clientX - panRef.current.x;
        const dy = e.touches[0].clientY - panRef.current.y;
        el.scrollLeft = panRef.current.scrollLeft - dx;
        el.scrollTop = panRef.current.scrollTop - dy;
      }
    };

    const onTouchEnd = (e) => {
      if (e.touches.length === 1) {
        panRef.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
          scrollLeft: el.scrollLeft,
          scrollTop: el.scrollTop,
        };
        return;
      }
      panRef.current = null;
    };

    const onWheel = (e) => {
      if (e.ctrlKey) {
        e.preventDefault();
        setZoom(zoomRef.current * (e.deltaY > 0 ? 0.95 : 1.05), e.clientX, e.clientY);
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("wheel", onWheel);
    };
  }, [minZoom]);

  useEffect(() => {
    if (!url) return;

    zoomRef.current = initialZoom;
    baseSizeRef.current = { width: 0, height: 0 };

    let cancelled = false;
    let retryTimer = null;
    const canvases = [];

    const load = async (attempt = 0) => {
      try {
        setStatus("loading");

        const response = await fetch(url);
        if (cancelled) return;

        const contentType = response.headers.get("content-type") || "";
        const isPdf = contentType.includes("application/pdf");

        if (!isPdf) {
          if (attempt < MAX_RETRIES) {
            retryTimer = setTimeout(() => {
              if (!cancelled) load(attempt + 1);
            }, RETRY_DELAY_MS);
            return;
          }
          if (!cancelled) setStatus("error");
          return;
        }

        const arrayBuffer = await response.arrayBuffer();
        if (cancelled) return;

        const pdf = await pdfJs.getDocument({ data: arrayBuffer }).promise;
        if (cancelled) return;

        setPageCount(pdf.numPages);

        const container = contentRef.current;
        if (!container) return;
        container.innerHTML = "";
        container.style.transform = "";
        container.style.width = "";
        container.style.height = "";

        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          if (cancelled) return;

          const viewport = page.getViewport({ scale: RENDER_SCALE });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width = "100%";
          canvas.style.height = "auto";
          canvas.style.display = "block";

          if (i < pdf.numPages) {
            canvas.style.marginBottom = "8px";
          }

          container.appendChild(canvas);
          canvases.push(canvas);

          await page.render({
            canvasContext: canvas.getContext("2d"),
            viewport,
          }).promise;
        }

        const base = {
          width: container.scrollWidth,
          height: container.scrollHeight,
        };
        baseSizeRef.current = base;

        container.style.width = `${base.width}px`;
        container.style.height = `${base.height}px`;
        container.style.transformOrigin = "0 0";
        container.style.transform = `scale(${initialZoom})`;

        const spacer = spacerRef.current;
        if (spacer) {
          spacer.style.width = `${base.width * initialZoom}px`;
          spacer.style.height = `${base.height * initialZoom}px`;
        }

        if (!cancelled) setStatus("ready");
      } catch (err) {
        console.error("MobilePdfViewer:", err);
        if (!cancelled) setStatus("error");
      }
    };

    load();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      canvases.forEach((c) => {
        c.width = 0;
        c.height = 0;
      });
    };
  }, [initialZoom, url]);

  return (
    <Box
      ref={wrapperRef}
      sx={{
        height: "100%",
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
        touchAction: "pan-x pan-y",
        overscrollBehavior: "contain",
        bgcolor: "#525659",
      }}
    >
      {status === "loading" && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
          gap={2}
        >
          <CircularProgress sx={{ color: "grey.300" }} />
          <Typography variant="body2" sx={{ color: "grey.400" }}>
            Preparing PDF…
          </Typography>
        </Box>
      )}

      {status === "error" && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <Typography variant="body2" sx={{ color: "grey.400" }}>
            Unable to load PDF
          </Typography>
        </Box>
      )}

      <Box
        ref={spacerRef}
        sx={{
          overflow: "visible",
          visibility: status === "ready" ? "visible" : "hidden",
          position: status === "ready" ? "relative" : "absolute",
          mx: "auto",
        }}
      >
        <Box
          ref={contentRef}
          sx={{
            px: compact ? 1.5 : 0.5,
            py: compact ? 1.5 : 1,
          }}
        />
      </Box>

      {status === "ready" && pageCount > 1 && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "center",
            color: "grey.400",
            py: 1,
          }}
        >
          {pageCount} pages
        </Typography>
      )}
    </Box>
  );
}
